import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  ArrowLeft, Calendar, Clock, MapPin, Users, Package, BookOpen,
  Award, CheckCircle, Tent, ChevronDown, ChevronUp, DollarSign, Star, ShoppingCart, Loader2, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { trackViewItem, trackAddToCart } from '../../utils/ga4';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CampDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, api } = useAuth();
  const [camp, setCamp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDays, setSelectedDays] = useState(new Set());
  const [selectedSection, setSelectedSection] = useState('');
  const [expandedDay, setExpandedDay] = useState(null);
  const [allDaysSelected, setAllDaysSelected] = useState(false);

  // Enrollment state
  const [enrollDialog, setEnrollDialog] = useState(false);
  const [learners, setLearners] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState('');
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    const fetchCamp = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API_URL}/api/camps/${id}/`, { headers });
        setCamp(response.data);
        const allDayNums = new Set(response.data.days.map(d => d.day_number));
        setSelectedDays(allDayNums);
        setAllDaysSelected(true);
        // GA4 Ecommerce — view_item on camp detail view.
        // item_category = "Summer Camp" (program type); item_category2 = first
        // section's location so reporting works even before a section is picked.
        trackViewItem({
          id: response.data.id,
          name: response.data.name,
          category: 'summer_camp',
          location: response.data.sections?.[0]?.location_name || null,
          grade_bands: response.data.grade_bands,
          price: Number(response.data.sections?.[0]?.price_full_camp || response.data.full_camp_price || 0),
        });
      } catch {
        console.error('Failed to fetch camp');
      } finally {
        setLoading(false);
      }
    };
    fetchCamp();
  }, [id]);

  // Fetch learners when authenticated
  useEffect(() => {
    if (isAuthenticated && api) {
      api.get('/learners/').then(r => {
        setLearners(r.data.results || r.data || []);
      }).catch(() => {});
    }
  }, [isAuthenticated, api]);

  const toggleDay = (dayNum) => {
    setSelectedDays(prev => {
      const next = new Set(prev);
      next.has(dayNum) ? next.delete(dayNum) : next.add(dayNum);
      setAllDaysSelected(next.size === camp.days.length);
      return next;
    });
  };

  const toggleAllDays = () => {
    if (allDaysSelected) {
      setSelectedDays(new Set());
      setAllDaysSelected(false);
    } else {
      setSelectedDays(new Set(camp.days.map(d => d.day_number)));
      setAllDaysSelected(true);
    }
  };

  const selectedSectionData = useMemo(() => {
    if (!selectedSection || !camp) return null;
    return camp.sections.find(s => s.id === selectedSection);
  }, [selectedSection, camp]);

  const totalPrice = useMemo(() => {
    if (!selectedSectionData || !camp) return 0;
    if (allDaysSelected && selectedSectionData.price_full_camp) {
      return Number(selectedSectionData.price_full_camp);
    }
    return selectedDays.size * Number(selectedSectionData.price_per_day || 0);
  }, [selectedDays, allDaysSelected, selectedSectionData, camp]);

  const totalKitPrice = useMemo(() => {
    if (!camp) return 0;
    return camp.days
      .filter(d => selectedDays.has(d.day_number) && d.has_kit && d.kit_required)
      .reduce((sum, d) => sum + Number(d.kit_price || 0), 0);
  }, [camp, selectedDays]);

  const handleEnrollClick = () => {
    // If the selected section has an external URL, redirect there
    if (selectedSectionData?.external_url) {
      window.open(selectedSectionData.external_url, '_blank');
      return;
    }
    if (!isAuthenticated) {
      toast.info('Please log in to enroll');
      navigate('/login');
      return;
    }
    if (learners.length === 0) {
      toast.info('Please add a learner to your account first');
      navigate('/parent/learners');
      return;
    }
    if (learners.length === 1) {
      setSelectedLearner(learners[0].id);
    }
    setEnrollDialog(true);
  };

  const handleAddToCart = async () => {
    if (!selectedLearner) {
      toast.error('Please select a learner');
      return;
    }
    setEnrolling(true);
    try {
      await api.post(`/camps/${camp.id}/add-to-cart/`, {
        camp_section_id: selectedSection,
        learner_id: selectedLearner,
        selected_days: Array.from(selectedDays),
      });
      // GA4 Ecommerce — add_to_cart (fires AFTER backend success)
      const section = camp.sections.find(s => s.id === selectedSection);
      const perDay = Number(section?.price_per_day || 0);
      const qty = Math.max(1, selectedDays.size);
      trackAddToCart(
        {
          id: camp.id,
          name: camp.name,
          category: 'summer_camp',
          location: section?.location_name || null,
          grade_bands: camp.grade_bands,
          price: perDay,
        },
        { quantity: qty },
      );
      toast.success('Camp added to cart!');
      setEnrollDialog(false);
      navigate('/cart');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add to cart');
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-10 w-10 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!camp) return <div className="text-center py-20 text-muted-foreground">Camp not found</div>;

  const dayColors = [
    'from-blue-500 to-blue-600',
    'from-emerald-500 to-emerald-600',
    'from-violet-500 to-violet-600',
    'from-amber-500 to-amber-600',
    'from-rose-500 to-rose-600',
  ];

  return (
    <div className="pb-20">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-800 via-slate-900 to-primary/30">
        <div className="container-app relative z-10 py-10 md:py-14 lg:py-16">
          <Link to="/summer-camps" className="inline-flex items-center text-sm text-white/60 hover:text-white mb-6 transition-colors" data-testid="back-to-camps">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Camps
          </Link>
          <div className="max-w-3xl">
            <div className="flex flex-wrap gap-2 mb-4">
              <Badge className="bg-yellow-400 text-yellow-900 hover:bg-yellow-400 font-semibold">
                <Tent className="h-3 w-3 mr-1" /> {camp.total_days}-Day Camp
              </Badge>
              {camp.grade_bands?.map(g => (
                <Badge key={g} variant="outline" className="border-white/40 text-white/90 bg-white/10 backdrop-blur-sm">{g}</Badge>
              ))}
              {camp.category && (
                <Badge variant="outline" className="border-white/40 text-white/90 bg-white/10 backdrop-blur-sm capitalize">{camp.category}</Badge>
              )}
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white" data-testid="camp-title">
              {camp.name}
            </h1>
            <p className="text-base md:text-lg text-white/80 leading-relaxed">{camp.description}</p>
            <div className="flex flex-wrap gap-4 mt-6">
              <div className="flex items-center gap-2 text-white/70 text-sm">
                <Calendar className="h-4 w-4" /> {camp.total_days} days of learning
              </div>
              <div className="flex items-center gap-2 text-white/70 text-sm">
                <MapPin className="h-4 w-4" /> {camp.sections.length} location{camp.sections.length !== 1 ? 's' : ''}
              </div>
              <div className="flex items-center gap-2 text-white/70 text-sm">
                <DollarSign className="h-4 w-4" /> Starting at ${camp.sections[0]?.price_per_day || '—'}/day
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container-app py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Day-by-Day */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold">Day-by-Day Curriculum</h2>
              <Button variant="outline" size="sm" onClick={toggleAllDays} data-testid="toggle-all-days">
                {allDaysSelected ? 'Deselect All' : 'Select All Days'}
              </Button>
            </div>

            {camp.days.map((day) => {
              const isSelected = selectedDays.has(day.day_number);
              const isExpanded = expandedDay === day.day_number;
              const colorClass = dayColors[(day.day_number - 1) % dayColors.length];

              return (
                <Card
                  key={day.id}
                  className={`overflow-hidden transition-all duration-200 ${isSelected ? 'ring-2 ring-primary shadow-md' : 'opacity-80'}`}
                  data-testid={`camp-day-${day.day_number}`}
                >
                  <div className="flex">
                    <div
                      className={`w-16 md:w-20 shrink-0 bg-gradient-to-b ${colorClass} flex flex-col items-center justify-center text-white relative overflow-hidden`}
                      style={day.thumbnail_url ? { backgroundImage: `url(${day.thumbnail_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
                    >
                      {day.thumbnail_url && <div className={`absolute inset-0 bg-gradient-to-b ${colorClass} opacity-60`} />}
                      <span className="text-xs font-medium uppercase tracking-wide relative z-10 drop-shadow-md">Day</span>
                      <span className="text-2xl md:text-3xl font-bold relative z-10 drop-shadow-md">{day.day_number}</span>
                    </div>
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleDay(day.day_number)}
                            className="mt-1"
                            data-testid={`select-day-${day.day_number}`}
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-base">{day.course_title}</h3>
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{day.course_description}</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <Badge variant="outline" className="text-xs">
                                ${day.kit_required ? (Number(day.course_price) + Number(day.kit_price || 0)).toFixed(2) : day.course_price}
                              </Badge>
                              {day.has_kit && day.kit_required && (
                                <Badge className="bg-green-100 text-green-700 text-xs">
                                  <Package className="h-3 w-3 mr-1" />
                                  {day.kit_name} Kit Included
                                </Badge>
                              )}
                              {day.has_kit && !day.kit_required && (
                                <Badge className="bg-gray-100 text-gray-600 text-xs">
                                  <Package className="h-3 w-3 mr-1" />
                                  {day.kit_name} (Optional) +${day.kit_price}
                                </Badge>
                              )}
                              {day.duration_minutes && (
                                <Badge variant="outline" className="text-xs">
                                  <Clock className="h-3 w-3 mr-1" /> {day.duration_minutes} min
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setExpandedDay(isExpanded ? null : day.day_number)} data-testid={`expand-day-${day.day_number}`}>
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                      </div>

                      {isExpanded && (
                        <div className="mt-4 space-y-4 border-t pt-4 animate-in fade-in duration-200">
                          {day.learning_outcomes?.length > 0 && (
                            <div>
                              <h4 className="font-medium text-sm flex items-center gap-1 mb-2">
                                <BookOpen className="h-4 w-4 text-primary" /> Learning Outcomes
                              </h4>
                              <ul className="space-y-1.5">
                                {day.learning_outcomes.map((lo, i) => (
                                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" /> {lo}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {day.state_standards?.length > 0 && (
                            <div>
                              <h4 className="font-medium text-sm flex items-center gap-1 mb-2">
                                <Award className="h-4 w-4 text-amber-500" /> State Standards
                              </h4>
                              <div className="flex flex-wrap gap-1.5">
                                {day.state_standards.map((ss, i) => (
                                  <Badge key={i} variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">{ss}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Right: Enrollment Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-4">
              <Card className="card-dashboard">
                <CardContent className="p-5 space-y-4">
                  <h3 className="font-heading font-bold text-lg">Enroll Now</h3>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Choose a Location</Label>
                    <Select value={selectedSection} onValueChange={setSelectedSection}>
                      <SelectTrigger data-testid="select-camp-location">
                        <SelectValue placeholder="Select location..." />
                      </SelectTrigger>
                      <SelectContent>
                        {camp.sections.map(s => (
                          <SelectItem key={s.id} value={s.id}>
                            <div className="flex items-center gap-2">
                              <MapPin className="h-3 w-3" />
                              {s.location_name}
                              {s.external_url ? (
                                <span className="text-blue-600 text-xs font-medium">(External)</span>
                              ) : (
                                <span className="text-muted-foreground text-xs">({s.spots_remaining} spots)</span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Selected Days ({selectedDays.size}/{camp.total_days})</Label>
                    <div className="flex flex-wrap gap-1">
                      {camp.days.map(d => (
                        <Badge
                          key={d.day_number}
                          className={`cursor-pointer transition-all text-xs ${
                            selectedDays.has(d.day_number) ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                          }`}
                          onClick={() => toggleDay(d.day_number)}
                        >
                          Day {d.day_number}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {selectedSectionData?.external_url && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                      <ExternalLink className="h-4 w-4 inline mr-1" />
                      Registration for this location is handled by an external partner. You will be redirected to their website.
                    </div>
                  )}

                  {selectedSection && selectedDays.size > 0 && !selectedSectionData?.external_url && (
                    <div className="border-t pt-4 space-y-2">
                      {allDaysSelected && selectedSectionData?.price_full_camp ? (
                        <>
                          <div className="flex justify-between text-sm">
                            <span>Full Camp ({camp.total_days} days)</span>
                            <span className="line-through text-muted-foreground">
                              ${(selectedDays.size * Number(selectedSectionData.price_per_day)).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm text-green-600 font-medium">
                            <span className="flex items-center gap-1"><Star className="h-3 w-3" /> Full Camp Discount</span>
                            <span>-${((selectedDays.size * Number(selectedSectionData.price_per_day)) - Number(selectedSectionData.price_full_camp)).toFixed(2)}</span>
                          </div>
                        </>
                      ) : (
                        <div className="flex justify-between text-sm">
                          <span>{selectedDays.size} day{selectedDays.size !== 1 ? 's' : ''} x ${selectedSectionData?.price_per_day}</span>
                          <span>${totalPrice.toFixed(2)}</span>
                        </div>
                      )}
                      {totalKitPrice > 0 && (
                        <div className="flex justify-between text-sm text-primary">
                          <span>+ STEM Kits</span>
                          <span>${totalKitPrice.toFixed(2)}</span>
                        </div>
                      )}
                      <hr className="my-2" />
                      <div className="flex justify-between font-bold text-lg">
                        <span>Total</span>
                        <span>${(totalPrice + totalKitPrice).toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  <Button
                    className={`w-full ${selectedSectionData?.external_url ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                    size="lg"
                    disabled={!selectedSection || selectedDays.size === 0}
                    onClick={handleEnrollClick}
                    data-testid="enroll-camp-btn"
                  >
                    {selectedSectionData?.external_url ? (
                      <><ExternalLink className="h-4 w-4 mr-2" /> Register on Partner Site</>
                    ) : (
                      <><ShoppingCart className="h-4 w-4 mr-2" />
                      {selectedDays.size === camp.total_days ? 'Enroll in Full Camp' : `Enroll in ${selectedDays.size} Day${selectedDays.size !== 1 ? 's' : ''}`}</>
                    )}
                  </Button>

                  {!selectedSection && (
                    <p className="text-xs text-center text-muted-foreground">Select a location to see pricing</p>
                  )}
                </CardContent>
              </Card>

              {selectedSectionData && (
                <Card className="card-dashboard">
                  <CardContent className="p-5 space-y-3">
                    <h4 className="font-medium text-sm">Location Details</h4>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {selectedSectionData.location_name}</div>
                      {selectedSectionData.start_date && (
                        <div className="flex items-center gap-2"><Calendar className="h-4 w-4" /> {selectedSectionData.start_date} — {selectedSectionData.end_date}</div>
                      )}
                      {selectedSectionData.start_time && (
                        <div className="flex items-center gap-2"><Clock className="h-4 w-4" /> {selectedSectionData.start_time} — {selectedSectionData.end_time}</div>
                      )}
                      <div className="flex items-center gap-2"><Users className="h-4 w-4" /> {selectedSectionData.spots_remaining} spots remaining</div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Enroll Dialog - Learner Selection */}
      <Dialog open={enrollDialog} onOpenChange={setEnrollDialog}>
        <DialogContent className="max-w-md" data-testid="enroll-dialog">
          <DialogHeader>
            <DialogTitle>Select Learner</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Choose which learner to enroll in <strong>{camp.name}</strong>
              {allDaysSelected ? ' (Full Camp)' : ` (${selectedDays.size} day${selectedDays.size !== 1 ? 's' : ''})`}
            </p>
            <Select value={selectedLearner} onValueChange={setSelectedLearner}>
              <SelectTrigger data-testid="select-learner">
                <SelectValue placeholder="Select a learner..." />
              </SelectTrigger>
              <SelectContent>
                {learners.map(l => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.first_name} {l.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
              <div className="flex justify-between"><span>Camp</span><span className="font-medium">{camp.name}</span></div>
              <div className="flex justify-between"><span>Days</span><span>{selectedDays.size} of {camp.total_days}</span></div>
              <div className="flex justify-between font-semibold text-base pt-1 border-t"><span>Total</span><span>${(totalPrice + totalKitPrice).toFixed(2)}</span></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrollDialog(false)}>Cancel</Button>
            <Button onClick={handleAddToCart} disabled={!selectedLearner || enrolling} data-testid="confirm-enroll-btn">
              {enrolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShoppingCart className="h-4 w-4 mr-2" />}
              Add to Cart
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
